import { AccordionGroup, AccordionItem } from '@theme/accordion';
import { events, publish, subscribe } from '@theme/event-bus';
import { classes, createScrolledByIntersectionObserver, on, updateSectionElements } from '@theme/helpers';
import { QuickAdd } from '@theme/quick-add';
import { SelectProvider } from '@theme/select-provider';
import { ToggleButton, ToggleElement } from '@theme/toggle-element';

/**
 * Keyboard events.
 */
on('keyup', document, (event) => {
  if (event.key === 'Escape') publish(events.escapeKey);
  if (event.key === 'Tab') document.body.classList.add(classes.tabbable);
});

/**
 * Custom elements.
 */
customElements.define('accordion-group', AccordionGroup);
customElements.define('accordion-item', AccordionItem);
customElements.define('quick-add', QuickAdd);
customElements.define('select-provider', SelectProvider);
customElements.define('toggle-element', ToggleElement);
customElements.define('toggle-button', ToggleButton);

/**
 * Handles Section Rendering API events.
 */
subscribe(events.sectionUpdate, async ({ sections, onAfterElementsUpdated }) => {
  await Promise.all(
    Object.entries(sections).map(([sectionId, sectionHtml]) => updateSectionElements(sectionId, sectionHtml)),
  );

  if (typeof onAfterElementsUpdated === 'function') {
    onAfterElementsUpdated();
  }
});

/**
 * Section intersections.
 */
const scrollObservers = {
  default: createScrolledByIntersectionObserver(),
  priority: createScrolledByIntersectionObserver(0),
};

/**
 * @param {HTMLElement} element
 */
const observeSection = (element) => {
  if (element.classList.contains('shopify-section-main')) {
    scrollObservers.priority.observe(element);
    return;
  }

  scrollObservers.default.observe(element);
};

/**
 * Theme editor support.
 */
document.addEventListener('shopify:section:load', (event) => {
  observeSection(event.target);
});

for (const section of [...document.querySelectorAll('.shopify-section')]) {
  observeSection(section);
}
